/**
 * Meeting Detail Page Types
 *
 * Story 3.5.6: Meeting Detail Page - Granola-Style Redesign
 *
 * Types for meeting notes, enhanced notes, transcript popovers,
 * and the meeting detail page components.
 *
 * NOTE: ToneTimelineEntry is imported from post-meeting-analytics.ts.
 * MeetingSuggestion is defined locally with a flat structure for UI display.
 */
import { z } from 'zod';
import { AttendeeResponseStatus, CalendarProvider } from './calendar.js';
import { DealAccessRequestStatus } from './deal-access-requests.js';
import { DEAL_MATCH_STRATEGIES } from './deal-match-strategy.js';
import { MeetingPlatform } from './meeting-toast.js';
import { CLIENT_TONE_VALUES, MAX_ATTACHMENT_STORAGE_SIZE_BYTES, MEETING_NOTE_IMAGE_MIME_TYPES, MeetingStatus, QualityTier, SpeakerSide, } from './meeting-analysis.js';
// Import ToneTimelineEntry from post-meeting-analytics.ts for use in this file
// Note: Do NOT re-export - it is already exported from that module
import { ActionItemActionType, ActionItemPriority } from './post-meeting-analytics.js';
import { CoachingCategory } from './coaching.js';
import { MeetingNoteDocumentSchema } from './tiptap-document.js';
// =============================================================================
// View Mode Types (different from post-meeting-analytics NotesViewMode)
// =============================================================================
// 'draft' = raw user notes, 'enhanced' = AI-enhanced notes
export const NotesViewMode = {
    DRAFT: 'draft',
    ENHANCED: 'enhanced',
};
export const NotesViewModeSchema = z.enum(Object.values(NotesViewMode));
// =============================================================================
// Note Bullet Indent Level
// =============================================================================
export const BulletIndent = {
    BULLET: 0, // • symbol
    SUB_BULLET: 1, // ○ symbol (indented)
};
export const TranscriptSourceRefSchema = z.object({
    startTimeMs: z.number().int().min(0),
    endTimeMs: z.number().int().min(0),
    transcriptSnippet: z.string().min(1),
    speaker: z.string().min(1),
    speakerSide: z.enum(Object.values(SpeakerSide)).optional(),
});
export const NoteBulletSchema = z.object({
    id: z.string().uuid(),
    text: z.string(),
    indent: z.union([z.literal(0), z.literal(1)]),
    isNew: z.boolean().optional(),
    sourceRef: TranscriptSourceRefSchema.optional(),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
});
export const NoteSectionSchema = z.object({
    id: z.string().uuid(),
    header: z.string().min(1),
    bullets: z.array(NoteBulletSchema),
});
export const MeetingNotesSchema = z.object({
    meetingId: z.string().uuid(),
    userId: z.string().uuid(),
    document: MeetingNoteDocumentSchema,
    revision: z.number().int().min(0),
    viewMode: NotesViewModeSchema,
    lastUpdatedAt: z.string().datetime(),
    readOnly: z.boolean().optional(),
    ownerUserId: z.string().uuid().nullable().optional(),
    runInProgress: z.boolean().optional(),
    activeRunId: z.string().uuid().nullable().optional(),
});
export const EnhancedSummaryPointSchema = z.object({
    id: z.string().uuid(),
    text: z.string().min(1),
    indent: z.union([z.literal(0), z.literal(1)]),
    sourceRef: TranscriptSourceRefSchema,
    isNew: z.boolean().optional(),
    type: z.enum(['text', 'image']).optional(),
    imageUrl: z.string().optional(),
    isFromDraft: z.boolean().optional(),
    isStandalone: z.boolean().optional(),
    contentParts: z
        .array(z.discriminatedUnion('type', [
        z.object({ type: z.literal('text'), text: z.string() }),
        z.object({ type: z.literal('file'), attachmentId: z.string() }),
        z.object({ type: z.literal('image'), attachmentId: z.string() }),
    ]))
        .optional(),
    userEdited: z.boolean().optional(),
});
export const EnhancedSectionSchema = z.object({
    id: z.string().uuid(),
    header: z.string().min(1),
    isFromDraft: z.boolean().optional(),
    points: z.array(EnhancedSummaryPointSchema),
});
export const MeetingEnhancedSummarySchema = z.object({
    meetingId: z.string().uuid(),
    document: MeetingNoteDocumentSchema,
    revision: z.number().int().min(0),
    isComplete: z.boolean(),
    generator: z.string(),
    generatedAt: z.string().datetime(),
    activeRunId: z.string().uuid().nullable(),
    readOnly: z.boolean().optional(),
    ownerUserId: z.string().uuid().nullable().optional(),
    previousDocument: MeetingNoteDocumentSchema.nullable().optional(),
});
export const MeetingSuggestionDisplaySchema = z.object({
    id: z.string().uuid(),
    content: z.string().min(1),
    category: z.string(),
    shownAtMs: z.number().int().min(0),
    wasUsed: z.boolean(),
    usages: z.array(z.object({ userId: z.string().uuid(), usedAtMs: z.number().int().min(0) })),
    transcriptExcerpt: z.string().optional(),
    speaker: z.string().optional(),
    knowledgeBaseReferences: z.array(z.object({
        articleId: z.string().min(1),
        title: z.string().min(1),
        tags: z.array(z.string()),
    })).optional(),
    clientTone: z.enum(CLIENT_TONE_VALUES).nullish(),
});
export const MeetingParticipantSchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    email: z.string().email().optional(),
    role: z.string().optional(),
    company: z.string().optional(),
    avatarUrl: z.string().url().optional(),
    side: z.enum(Object.values(SpeakerSide)),
});
export const MeetingParticipantSessionSchema = z.object({
    userId: z.string().uuid(),
    displayName: z.string().min(1),
    email: z.string().email().nullable(),
    role: z.string().nullable(),
    avatarUrl: z.string().url().optional(),
    sessionIndex: z.number().int().min(0),
    joinedAt: z.string(),
    leftAt: z.string().nullable(),
    durationMs: z.number().int().nullable(),
});
export const MeetingAttendeeSchema = z.object({
    email: z.string().email(),
    name: z.string().optional(),
    responseStatus: z.enum(Object.values(AttendeeResponseStatus)).optional(),
    isOrganizer: z.boolean().optional(),
    isAppUser: z.boolean().optional(),
    appUserId: z.string().uuid().optional(),
    appUserRole: z.string().optional(),
});
export const StrategicCoachingResultSchema = z.object({
    trend: z.enum(['improving', 'declining', 'stable']),
    currentScore: z.number().min(0).max(100),
    scoreHistory: z.array(z.object({
        meetingId: z.string().uuid(),
        meetingTitle: z.string(),
        date: z.string(),
        score: z.number().min(0).max(100),
    })),
    focusAreas: z.array(z.object({
        area: z.string(),
        trend: z.enum(['improving', 'declining', 'stable']),
        suggestion: z.string(),
    })),
    strategicSuggestions: z.array(z.string()),
    skipped: z.boolean(),
    skipMessage: z.string().optional(),
    generatedAt: z.string().datetime(),
});
export const MeetingAnalysisTagSchema = z.object({
    label: z.string().min(1),
    type: z.enum(['strategy', 'opportunity', 'action', 'risk', 'info']),
});
export const MeetingDealContextSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    value: z.number().nullable(),
    stage: z.string(),
    clientName: z.string(),
    lifecycle: z.enum(['open', 'closed', 'archived', 'deleted']).optional(),
    dealMatchStrategy: z.enum(DEAL_MATCH_STRATEGIES).nullable().optional(),
    dealConfidenceScore: z.number().min(0).max(100).nullable().optional(),
    dealLinkReason: z.string().nullable().optional(),
});
export const InaccessibleMeetingDealContextSchema = z.object({
    id: z.string().uuid(),
    clientName: z.string().nullable(),
    lifecycle: z.enum(['active', 'archived', 'deleted']).optional(),
    clientLogoUrl: z.string().nullable().optional(),
    reason: z.literal('not_collaborator'),
    accessRequestStatus: z.enum(Object.values(DealAccessRequestStatus)).optional(),
});
export const MeetingEmailThreadSchema = z.object({
    id: z.string(),
    subject: z.string(),
    lastMessagePreview: z.string(),
    lastMessageAt: z.string().datetime(),
    messageCount: z.number().int().min(1),
    participants: z.array(z.string()),
});
export const MeetingCreatedFromActionItemSchema = z.object({
    id: z.string().uuid(),
    content: z.string().min(1),
    isCompleted: z.boolean(),
});
export const MeetingActionItemSchema = z.object({
    id: z.string().uuid(),
    content: z.string().min(1),
    isCompleted: z.boolean(),
    priority: z.enum(Object.values(ActionItemPriority)),
    category: z.string(),
    dueDate: z.string().datetime().optional(),
    source: z.enum(['ai', 'user']),
    actionType: z.enum(Object.values(ActionItemActionType)).nullable().optional(),
});
export const TranscriptSegmentDisplaySchema = z.object({
    id: z.string(),
    speaker: z.string(),
    speakerSide: z.enum(Object.values(SpeakerSide)),
    displayName: z.string().nullable().optional(),
    userId: z.string().nullable().optional(),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(0),
    text: z.string(),
    sessionIndex: z.number().int().min(0),
    diarizationEnabled: z.boolean().optional(),
    timelineStartMs: z.number().int().min(0).optional(),
    timelineEndMs: z.number().int().min(0).optional(),
});
// ToneTimelineEntry interface is imported from post-meeting-analytics.ts
export const ToneTimelineEntrySchema = z.object({
    timestampMs: z.number().int().min(0),
    sessionIndex: z.number().int().min(0).default(0),
    userId: z.string().uuid().nullable().optional(),
    speaker: z.string(),
    textSentiment: z.number().min(-1).max(1),
    stressScore: z.number().min(0).max(1),
    transcriptText: z.string().optional(),
});
/**
 * Human label parsed from RRULE FREQ=. `custom` covers anything we cannot
 * map (no FREQ token, unsupported value, etc).
 */
export const RecurrenceLabel = {
    WEEKLY: 'weekly',
    DAILY: 'daily',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
    CUSTOM: 'custom',
};
export const RecurrenceLabelSchema = z.enum(Object.values(RecurrenceLabel));
export const MeetingSessionSchema = z.object({
    sessionIndex: z.number().int().min(0),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    durationMs: z.number().int().nullable(),
});
export const MeetingDetailDataSchema = z.object({
    id: z.string().uuid(),
    meetingIdentity: z.string().nullable(),
    clientId: z.string().uuid().nullable(),
    clientName: z.string().nullable().optional(),
    clientLogoUrl: z.string().url().nullable().optional(),
    clientLifecycle: z.enum(['active', 'archived', 'deleted']).optional(),
    calendarEventId: z.string().uuid().nullable(),
    canModifyEvent: z.boolean().optional(),
    sourceStatus: z.enum(['active', 'deleted']),
    sourceDeletedAt: z.string().datetime().nullable(),
    sourceExternalId: z.string().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    meetingLink: z.string().url().nullable(),
    provider: z.enum(Object.values(CalendarProvider)).nullable(),
    organizerEmail: z.string().email().nullable(),
    attendees: z.array(MeetingAttendeeSchema).nullable(),
    location: z.string().nullable(),
    meetingPlatform: z.enum(Object.values(MeetingPlatform)).nullable(),
    isRecurring: z.boolean().optional(),
    recurringEventId: z.string().nullable(),
    seriesSequence: z.number().int().nullable(),
    seriesTotal: z.number().int().nullable(),
    recurrenceLabel: RecurrenceLabelSchema.nullable(),
    seriesFirstStart: z.string().nullable(),
    startedAt: z.string().nullable(),
    endedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().min(0).nullable(),
    scheduledStart: z.string().nullable(),
    scheduledEnd: z.string().nullable(),
    isAllDay: z.boolean().optional(),
    sessions: z.array(MeetingSessionSchema),
    status: z.enum(Object.values(MeetingStatus)),
    participants: z.array(MeetingParticipantSchema),
    participantSessions: z.array(MeetingParticipantSessionSchema),
    deal: MeetingDealContextSchema.nullable(),
    inaccessibleDeal: InaccessibleMeetingDealContextSchema.nullable().optional(),
    userNotes: MeetingNotesSchema.nullable(),
    enhancedSummary: MeetingEnhancedSummarySchema.nullable(),
    actionItems: z.array(MeetingActionItemSchema),
    createdFromActionItem: MeetingCreatedFromActionItemSchema.nullable(),
    nextSteps: z.array(z.string()),
    riskSignals: z.array(z.string()),
    suggestions: z.array(MeetingSuggestionDisplaySchema),
    toneTimeline: z.array(ToneTimelineEntrySchema),
    transcript: z.array(TranscriptSegmentDisplaySchema),
    emailThreads: z.array(MeetingEmailThreadSchema),
    qualityScore: z.number().min(0).max(100).nullable(),
    qualityTier: z.enum(Object.values(QualityTier)).nullable(),
    overallSentiment: z.enum(['positive', 'neutral', 'negative']).nullable(),
    sentimentConfidence: z.number().min(0).max(1).nullable().optional(),
    sentimentSampleSize: z.number().int().min(0).optional(),
    talkRatio: z
        .object({
        sales: z.number().min(0).max(100),
        client: z.number().min(0).max(100),
    })
        .nullable(),
    executiveRecap: z.string().nullable(),
    dealOutcome: z.string().nullable(),
    overallTone: z.string().nullable(),
    strategicCoaching: StrategicCoachingResultSchema.nullable(),
    memoryScopeCoverage: z
        .object({
        rep: z.object({ ingested: z.number().int().min(0) }),
        contact: z.object({
            ingested: z.number().int().min(0),
            skipped: z.number().int().min(0),
        }),
        deal: z.object({
            ingested: z.number().int().min(0),
            skipped: z.number().int().min(0),
        }),
        client: z.object({
            ingested: z.number().int().min(0),
            skipped: z.number().int().min(0),
        }),
        rep_client: z.object({
            ingested: z.number().int().min(0),
            skipped: z.number().int().min(0),
        }),
        sweetSpots: z.object({
            ingested: z.number().int().min(0),
            skippedDeal: z.number().int().min(0),
            skippedClient: z.number().int().min(0),
        }),
        hasUnlinkedScopes: z.boolean(),
    })
        .nullable(),
    analysisGeneration: z.number().int().nullable(),
    accessTier: z.enum(['owner', 'tier1', 'tier2']).optional(),
    meetingOwner: z
        .object({
        userId: z.string().uuid(),
        name: z.string().nullable(),
    })
        .optional(),
});
export const SaveMeetingDetailNotesRequestSchema = z.object({
    document: MeetingNoteDocumentSchema,
    baseRevision: z.number().int().min(0),
    viewMode: NotesViewModeSchema.optional(),
});
export const SaveEnhancedSummaryRequestSchema = z.object({
    document: MeetingNoteDocumentSchema,
    baseRevision: z.number().int().min(0),
    deletedIds: z.array(z.string()).optional(),
});
export const SaveMeetingDetailNotesResponseSchema = z.object({
    success: z.boolean(),
    notes: MeetingNotesSchema,
});
export const UpdateMeetingTitleRequestSchema = z.object({
    title: z.string().min(1).max(200),
});
/**
 * SSE event types for enhancement streaming
 */
export const EnhancementSSEEventType = {
    ENHANCEMENT_START: 'enhancement_start',
    PROGRESS: 'progress',
    SECTION_START: 'section_start',
    POINT_COMPLETE: 'point_complete',
    SECTION_COMPLETE: 'section_complete',
    ACTION_ITEM: 'action_item',
    ENHANCEMENT_COMPLETE: 'enhancement_complete',
    ERROR: 'error',
};
export const MeetingNoteImageSchema = z.object({
    id: z.string().uuid(),
    meetingId: z.string().uuid(),
    filename: z.string().min(1),
    fileType: z.string().refine((value) => MEETING_NOTE_IMAGE_MIME_TYPES.includes(value), {
        message: 'Unsupported meeting image MIME type',
    }),
    sizeBytes: z.number().int().positive(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    url: z.string().url(),
    createdAt: z.string().datetime(),
});
export const MeetingImageUploadRequestSchema = z.object({
    filename: z.string().min(1).max(255),
    fileType: z.string().refine((value) => MEETING_NOTE_IMAGE_MIME_TYPES.includes(value), {
        message: 'Unsupported meeting image MIME type',
    }),
    // Storage accepts up to 2GB. The analysis pipeline separately skips images
    // above its configured Gemini analysis ceiling.
    sizeBytes: z.number().int().positive().max(MAX_ATTACHMENT_STORAGE_SIZE_BYTES),
});
export const MeetingImageUploadResponseSchema = z.object({
    uploadUrl: z.string().url(),
    s3Key: z.string(),
    imageId: z.string().uuid(),
});
export const MeetingImageConfirmRequestSchema = z.object({
    imageId: z.string().uuid(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
});
export const MeetingImageConfirmResponseSchema = z.object({
    success: z.boolean(),
    image: MeetingNoteImageSchema,
});
export const MeetingImagesListResponseSchema = z.object({
    images: z.array(MeetingNoteImageSchema),
});
export const CoachingSegmentFeedbackSchema = z.object({
    id: z.string().uuid(),
    transcriptSnippet: z.string(),
    timestampMs: z.number().int().min(0),
    category: z.enum(Object.values(CoachingCategory)),
    issue: z.string(),
    whatYouSaid: z.string(),
    whatTopPerformersSay: z.string(),
    improvement: z.string(),
    relatedKBArticleId: z.string().uuid().optional(),
    relatedKBArticleTitle: z.string().optional(),
});
export const CoachingCategoryBreakdownSchema = z.object({
    category: z.enum(Object.values(CoachingCategory)),
    score: z.number().min(0).max(100),
    feedbackCount: z.number().int().min(0),
});
export const CoachingAnalysisResponseSchema = z.object({
    meetingId: z.string().uuid(),
    segments: z.array(CoachingSegmentFeedbackSchema),
    categoryBreakdown: z.array(CoachingCategoryBreakdownSchema),
    overallScore: z.number().min(0).max(100),
    generatedAt: z.string().datetime(),
    applicable: z.boolean().optional(),
});
export const CoachingAnalysisRequestSchema = z.object({
    forceRegenerate: z.boolean().optional(),
});
